const FLUSH_INTERVAL_MS = 10_000

export interface WizardPriceBatcherOptions<V> {
  onFlush: (updates: Record<string, V>) => void
  flushIntervalMs?: number
  /** Injectable for tests — defaults to the real clock/timer. */
  now?: () => number
  setTimeoutFn?: (fn: () => void, delay: number) => ReturnType<typeof setTimeout>
  clearTimeoutFn?: (id: ReturnType<typeof setTimeout>) => void
}

// Batches incoming price ticks and delivers them on a leading (first tick
// ever seen *for that specific key*, so the UI isn't blank while waiting) +
// trailing (every `flushIntervalMs` after that, measured from the last
// flush — not from the last message — so a steady trickle of messages can't
// push the interval out indefinitely) schedule.
//
// The leading flush is tracked per-key, not once per batcher session. A
// session-level flag would mean: once ANY key has triggered the one leading
// flush, every symbol that becomes newly subscribed afterward — e.g. a
// second, corrected range subscribe fired shortly after an initial guess —
// inherits whatever's left of the *previous* symbols' trailing window
// (which can be most of the 10s) before it ever shows a price, even though
// it was never flushed itself. Tracking "seen" per-key means a genuinely
// new symbol always gets its own immediate flush the first time it arrives,
// regardless of what the rest of the batch's timer state looks like.
//
// Framework-independent so it can be unit tested without rendering a
// component; `use-watchlist-wizard-prices.ts` wraps one of these per hook
// instance. Generic over the per-key value (V) so it can carry more than a
// bare price string (e.g. price + 24h change %) without the batcher needing
// to know its shape.
export class WizardPriceBatcher<V> {
  private readonly onFlush: (updates: Record<string, V>) => void
  private readonly flushIntervalMs: number
  private readonly now: () => number
  private readonly setTimeoutFn: NonNullable<WizardPriceBatcherOptions<V>['setTimeoutFn']>
  private readonly clearTimeoutFn: NonNullable<WizardPriceBatcherOptions<V>['clearTimeoutFn']>

  private pending = new Map<string, V>()
  private timeoutId: ReturnType<typeof setTimeout> | null = null
  private seen = new Set<string>()
  private lastFlushAt = 0

  constructor(options: WizardPriceBatcherOptions<V>) {
    this.onFlush = options.onFlush
    this.flushIntervalMs = options.flushIntervalMs ?? FLUSH_INTERVAL_MS
    this.now = options.now ?? (() => Date.now())
    // Storing `setTimeout`/`clearTimeout` as bare references and later
    // calling them as `this.setTimeoutFn(...)` invokes them with `this`
    // bound to the batcher instance instead of `window`, which browsers
    // reject with "Illegal invocation" — wrap them so they're always called
    // as detached global calls.
    this.setTimeoutFn = options.setTimeoutFn ?? ((fn, delay) => setTimeout(fn, delay))
    this.clearTimeoutFn = options.clearTimeoutFn ?? ((id) => clearTimeout(id))
  }

  ingest(entries: Array<[string, V]>): void {
    if (entries.length === 0) return
    let hasUnseenKey = false
    for (const [key, value] of entries) {
      this.pending.set(key, value)
      if (!this.seen.has(key)) hasUnseenKey = true
    }
    // A batch with even one never-before-flushed key flushes immediately —
    // whatever else is piggybacking in `pending` goes out a little early
    // too, which is harmless (it's reference data, not a correctness
    // issue); the alternative (holding a brand-new symbol hostage to an
    // unrelated trailing timer) is the actual bug this is fixing.
    if (hasUnseenKey) {
      this.flush()
      return
    }
    this.scheduleTrailingFlush()
  }

  /** Clears timer + pending buffer + per-key seen state. Does NOT call
   * `onFlush` — the caller decides separately whether to clear whatever it
   * displayed from prior flushes. */
  reset(): void {
    if (this.timeoutId !== null) this.clearTimeoutFn(this.timeoutId)
    this.timeoutId = null
    this.pending.clear()
    this.seen.clear()
  }

  /** Same as `reset`, kept as a distinct name for call sites (unmount) that
   * only ever want timer/pending teardown, never a display reset. */
  dispose(): void {
    this.reset()
  }

  private scheduleTrailingFlush(): void {
    if (this.timeoutId !== null) return
    const elapsed = this.now() - this.lastFlushAt
    const delay = Math.max(0, this.flushIntervalMs - elapsed)
    this.timeoutId = this.setTimeoutFn(() => {
      this.timeoutId = null
      this.flush()
    }, delay)
  }

  private flush(): void {
    const updates = Object.fromEntries(this.pending)
    this.pending.clear()
    this.lastFlushAt = this.now()
    const keys = Object.keys(updates)
    if (keys.length === 0) return
    for (const key of keys) this.seen.add(key)
    this.onFlush(updates)
  }
}
